<template>
  <div class="heures-services-form">
    <div v-if="loading" class="loading-indicator">
      <div class="spinner"></div>
      <p>Chargement en cours...</p>
    </div>
    
    <div v-else class="form-container">
      <div class="jours-container">
        <div 
          v-for="jour in jours" 
          :key="jour" 
          class="jour-tab" 
          :class="{ active: selectedJour === jour }"
          @click="selectJour(jour)"
        >
          {{ formatJour(jour) }}
        </div>
      </div>
      
      <div class="parametres-form">
        <h3>Paramètres pour {{ formatJour(selectedJour) }}</h3>
        
        <div class="form-group">
          <label>
            <input 
              type="checkbox" 
              v-model="formData.est_ouvrable" 
              @change="updateEstOuvrable"
            />
            Jour ouvrable
          </label>
        </div>
        
        <div v-if="formData.est_ouvrable" class="form-grid">
          <div class="form-group">
            <label for="heure_debut">Heure de début</label>
            <input 
              type="time" 
              id="heure_debut" 
              v-model="formData.heure_debut" 
              class="form-control"
            />
          </div>
          
          <div class="form-group">
            <label for="heure_fin">Heure de fin</label>
            <input 
              type="time" 
              id="heure_fin" 
              v-model="formData.heure_fin" 
              class="form-control"
            />
          </div>
          
          <div class="form-group">
            <label for="tolerance_retard">Tolérance retard (minutes)</label>
            <input 
              type="number" 
              id="tolerance_retard" 
              v-model.number="formData.tolerance_retard" 
              class="form-control"
              min="0"
              max="120"
            />
          </div>
          
          <div class="form-group">
            <label for="pause_debut">Début de pause</label>
            <input 
              type="time" 
              id="pause_debut" 
              v-model="formData.pause_debut" 
              class="form-control"
            />
          </div>
          
          <div class="form-group">
            <label for="pause_fin">Fin de pause</label>
            <input 
              type="time" 
              id="pause_fin" 
              v-model="formData.pause_fin" 
              class="form-control"
            />
          </div>
        </div>
        
        <div class="form-actions">
          <button 
            @click="saveParametres" 
            class="btn btn-primary"
            :disabled="loading"
          >
            <span class="material-icons">save</span>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import api from '../../services/api.js';

// Props
const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['reload', 'success']);

// Variables réactives
const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const selectedJour = ref('lundi');
const formData = reactive({
  jour: 'lundi',
  heure_debut: '08:00',
  heure_fin: '16:30',
  tolerance_retard: 30,
  pause_debut: '12:00',
  pause_fin: '13:00',
  est_ouvrable: true
});
const isLoading = ref(false);

// Formater le jour pour l'affichage
function formatJour(jour) {
  return jour.charAt(0).toUpperCase() + jour.slice(1);
}

// Sélectionner un jour
async function selectJour(jour) {
  selectedJour.value = jour;
  await loadJourParametres(jour);
}

// Charger les paramètres pour un jour spécifique
async function loadJourParametres(jour) {
  isLoading.value = true;
  
  try {
    const response = await api.get(`/api/parametres/${jour}`);
    
    if (response.data && response.data.success) {
      const data = response.data.data;
      
      if (data) {
        // Formater les heures pour l'input time (HH:MM)
        formData.jour = jour;
        formData.heure_debut = data.heure_debut ? data.heure_debut.substring(0, 5) : '08:00';
        formData.heure_fin = data.heure_fin ? data.heure_fin.substring(0, 5) : '16:30';
        formData.tolerance_retard = data.tolerance_retard || 30;
        formData.pause_debut = data.pause_debut ? data.pause_debut.substring(0, 5) : '12:00';
        formData.pause_fin = data.pause_fin ? data.pause_fin.substring(0, 5) : '13:00';
        formData.est_ouvrable = data.est_ouvrable !== undefined ? data.est_ouvrable : true;
      } else {
        // Valeurs par défaut si aucun paramètre n'existe pour ce jour
        resetForm(jour);
      }
    }
  } catch (error) {
    console.error(`Erreur lors du chargement des paramètres pour ${jour}:`, error);
    showNotification('error', 'Erreur', `Impossible de charger les paramètres pour ${formatJour(jour)}`);
    resetForm(jour);
  } finally {
    isLoading.value = false;
  }
}

// Réinitialiser le formulaire
function resetForm(jour) {
  formData.jour = jour;
  formData.heure_debut = '08:00';
  formData.heure_fin = '16:30';
  formData.tolerance_retard = 30;
  formData.pause_debut = '12:00';
  formData.pause_fin = '13:00';
  formData.est_ouvrable = jour !== 'samedi' && jour !== 'dimanche';
}

// Mettre à jour l'état ouvrable
function updateEstOuvrable() {
  if (!formData.est_ouvrable) {
    formData.pause_debut = null;
    formData.pause_fin = null;
  } else if (!formData.pause_debut && !formData.pause_fin) {
    formData.pause_debut = '12:00';
    formData.pause_fin = '13:00';
  }
}

// Enregistrer les paramètres
async function saveParametres() {
  isLoading.value = true;
  
  try {
    // Préparer les données à envoyer
    const dataToSend = {
      ...formData
    };
    
    // Si le jour n'est pas ouvrable, mettre les pauses à null
    if (!dataToSend.est_ouvrable) {
      dataToSend.pause_debut = null;
      dataToSend.pause_fin = null;
    }
    
    const response = await api.put(`/api/parametres/${selectedJour.value}`, dataToSend);
    
    if (response.data && response.data.success) {
      showNotification('success', 'Succès', `Paramètres pour ${formatJour(selectedJour.value)} enregistrés avec succès`);
      emit('success', { type: 'update', message: `Paramètres pour ${formatJour(selectedJour.value)} mis à jour` });
    }
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement des paramètres:', error);
    showNotification('error', 'Erreur', `Impossible d'enregistrer les paramètres: ${error.message}`);
  } finally {
    isLoading.value = false;
  }
}

// Afficher une notification
function showNotification(type, title, message) {
  // Cette fonction sera implémentée par le composant parent
  emit('success', { type, title, message });
}

// Charger les données au montage du composant
onMounted(async () => {
  await loadJourParametres(selectedJour.value);
});
</script>

<style scoped>
.heures-services-form {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #9C27B0;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.jours-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.jour-tab {
  padding: 10px 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.jour-tab:hover {
  background-color: #e0e0e0;
}

.jour-tab.active {
  background-color: #9C27B0;
  color: white;
}

.parametres-form {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.form-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
}

.btn-primary {
  background-color: #9C27B0;
  color: white;
}

.btn-primary:hover {
  background-color: #7B1FA2;
}

.btn-primary:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
</style>
